import {
  useEffect,
  useState
} from "react";

import {
  getOnetQuestions,
  getOnetResults
} from "../api/onetApi";

function OnetAttribution() {
  return (
    <div className="mt-4 rounded-2xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:items-center sm:text-left">
        <a
          href="https://services.onetcenter.org/"
          title="This site incorporates information from O*NET Web Services. Click to learn more."
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0"
        >
          <img
            src="https://www.onetcenter.org/image/link/onet-in-it.svg"
            style={{ width: "130px", height: "60px", border: "none" }}
            alt="O*NET in-it"
          />
        </a>

        <p className="max-w-3xl text-xs leading-relaxed text-white/50">
          This site incorporates information from{" "}
          <a
            href="https://services.onetcenter.org/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[#EB6AB5] underline underline-offset-2 hover:text-[#ff83d1]"
          >
            O*NET Web Services
          </a>{" "}
          by the U.S. Department of Labor, Employment and Training
          Administration (USDOL/ETA). O*NET® is a trademark of USDOL/ETA.
        </p>
      </div>
    </div>
  );
}

export default function
OnetInterestProfiler({
  initialResult = null,
  initialAnswers = {},
  onAnswersChange,
  onComplete
}) {
  const [
    loading,
    setLoading
  ] = useState(true);

  const [
    submitting,
    setSubmitting
  ] = useState(false);

  const [
    error,
    setError
  ] = useState("");

  const [
    questions,
    setQuestions
  ] = useState([]);

  const [
    options,
    setOptions
  ] = useState([]);

  const [
    answers,
    setAnswers
  ] = useState(initialAnswers || {});

  const [
    result,
    setResult
  ] = useState(initialResult);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const data =
          await getOnetQuestions();

        setQuestions(
          data?.question || []
        );

        setOptions(
          data?.answer_option || []
        );
      } catch (err) {
        setError(
          err?.message ||
            "Unable to load O*NET Interest Profiler."
        );
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  useEffect(() => {
    setAnswers(initialAnswers || {});
  }, [initialAnswers]);


  const answeredCount =
    Object.keys(answers).length;

  const totalQuestions =
    questions.length;

  const complete =
    totalQuestions > 0 &&
    answeredCount ===
      totalQuestions;

  const progress =
    totalQuestions
      ? Math.round(
          (
            answeredCount /
            totalQuestions
          ) * 100
        )
      : 0;

  const handleAnswer =
    (
      questionIndex,
      value
    ) => {
      const nextAnswers = {
        ...answers,
        [questionIndex]: Number(value)
      };

      setAnswers(nextAnswers);
      onAnswersChange?.(nextAnswers);
    };

  const submitProfiler =
    async () => {
      if (!complete) {
        setError(
          "Please answer all questions before continuing."
        );

        return;
      }

      try {
        setSubmitting(true);
        setError("");

        const orderedAnswers =
          questions
            .slice()
            .sort(
              (a, b) =>
                a.index -
                b.index
            )
            .map(
              (question) =>
                answers[
                  question.index
                ]
            );

        const data =
          await getOnetResults(
            orderedAnswers
          );

        setResult(data);

        onComplete?.(data);
      } catch (err) {
        setError(
          err?.message ||
            "Unable to calculate your O*NET profile."
        );
      } finally {
        setSubmitting(false);
      }
    };

  if (loading) {
    return (
      <div className="rounded-2xl border border-[#EB6AB5]/20 bg-white/5 p-6">
        <p className="text-white/70">
          Loading O*NET Interest
          Profiler...
        </p>
      </div>
    );
  }

  if (result) {
    return (
      <OnetResult
        result={result}
      />
    );
  }

  return (
    <section
      className="
        rounded-3xl
        border
        border-[#EB6AB5]/25
        bg-[#11111f]
        p-5
        sm:p-7
      "
    >
      <div>
        <p
          className="
            text-xs
            font-bold
            uppercase
            tracking-[0.2em]
            text-[#EB6AB5]
          "
        >
          O*NET Interest Profiler
        </p>

        <h3
          className="
            mt-2
            text-2xl
            font-extrabold
            text-white
          "
        >
          Discover the type of work
          that fits you
        </h3>

        <p
          className="
            mt-3
            max-w-3xl
            text-sm
            leading-relaxed
            text-white/60
          "
        >
          Rate how much you would
          like or dislike each
          activity. There are no
          right or wrong answers.
        </p>
      </div>

      <OnetAttribution />

      <div className="mt-5">
        <div
          className="
            flex
            items-center
            justify-between
            text-xs
            text-white/50
          "
        >
          <span>
            {answeredCount} of{" "}
            {totalQuestions} answered
          </span>

          <span>
            {progress}%
          </span>
        </div>

        <div
          className="
            mt-2
            h-2
            overflow-hidden
            rounded-full
            bg-white/10
          "
        >
          <div
            className="
              h-full
              rounded-full
              bg-[#EB6AB5]
              transition-all
            "
            style={{
              width:
                `${progress}%`
            }}
          />
        </div>
      </div>

      {error && (
        <div
          className="
            mt-5
            rounded-xl
            border
            border-red-400/20
            bg-red-500/10
            p-4
            text-sm
            text-red-300
          "
        >
          {error}
        </div>
      )}

      <div className="mt-7 space-y-6">
        {questions.map(
          (
            question,
            index
          ) => (
            <article
              key={question.index}
              className="
                rounded-2xl
                border
                border-white/10
                bg-black/20
                p-5
              "
            >
              <p
                className="
                  text-xs
                  font-semibold
                  uppercase
                  tracking-wider
                  text-[#EB6AB5]/70
                "
              >
                Question{" "}
                {index + 1}
              </p>

              <h4
                className="
                  mt-2
                  text-base
                  font-semibold
                  text-white
                "
              >
                {question.text}
              </h4>

              <div
                className="
                  mt-4
                  grid
                  grid-cols-1
                  sm:grid-cols-5
                  gap-2
                "
              >
                {options.map(
                  (option) => {
                    const selected =
                      answers[
                        question.index
                      ] ===
                      option.value;

                    return (
                      <button
                        key={
                          option.value
                        }
                        type="button"
                        onClick={() =>
                          handleAnswer(
                            question.index,
                            option.value
                          )
                        }
                        className={`
                          rounded-xl
                          border
                          px-3
                          py-3
                          text-sm
                          font-semibold
                          transition
                          ${
                            selected
                              ? "border-[#EB6AB5] bg-[#EB6AB5] text-[#0F0A24]"
                              : "border-white/10 bg-white/5 text-white/70 hover:border-[#EB6AB5]/50"
                          }
                        `}
                      >
                        {option.name}
                      </button>
                    );
                  }
                )}
              </div>
            </article>
          )
        )}
      </div>

      <button
        type="button"
        disabled={
          !complete ||
          submitting
        }
        onClick={
          submitProfiler
        }
        className="
          mt-7
          w-full
          rounded-full
          bg-[#EB6AB5]
          px-7
          py-3.5
          font-bold
          text-[#0F0A24]
          transition
          hover:bg-[#ff83d1]
          disabled:cursor-not-allowed
          disabled:opacity-40
        "
      >
        {submitting
          ? "Calculating profile..."
          : "Calculate My O*NET Profile →"}
      </button>
    </section>
  );
}

function OnetResult({
  result
}) {
  const top3 =
    result?.top3 || [];

  const details =
    result?.details || [];

  return (
    <section
      className="
        rounded-3xl
        border
        border-[#EB6AB5]/30
        bg-[#11111f]
        p-6
      "
    >
      <p
        className="
          text-xs
          font-bold
          uppercase
          tracking-[0.2em]
          text-[#EB6AB5]
        "
      >
        O*NET Interest Profile
      </p>

      <h3
        className="
          mt-2
          text-2xl
          font-extrabold
          text-white
        "
      >
        Your strongest interests
      </h3>

      <OnetAttribution />

      <div
        className="
          mt-5
          flex
          flex-wrap
          gap-3
        "
      >
        {top3.map(
          (
            item,
            index
          ) => (
            <span
              key={item}
              className="
                rounded-full
                border
                border-[#EB6AB5]/30
                bg-[#EB6AB5]/10
                px-5
                py-2
                font-bold
                capitalize
                text-[#EB6AB5]
              "
            >
              #{index + 1}{" "}
              {item}
            </span>
          )
        )}
      </div>

      <div
        className="
          mt-6
          grid
          gap-3
          sm:grid-cols-2
          lg:grid-cols-3
        "
      >
        {details.map(
          (item) => (
            <div
              key={item.code}
              className="
                rounded-xl
                border
                border-white/10
                bg-black/20
                p-4
              "
            >
              <p
                className="
                  text-sm
                  font-bold
                  text-white
                "
              >
                {item.title}
              </p>

              <strong
                className="
                  mt-2
                  block
                  text-3xl
                  text-[#EB6AB5]
                "
              >
                {item.score}
              </strong>

              <p
                className="
                  mt-2
                  text-xs
                  leading-relaxed
                  text-white/50
                "
              >
                {item.description}
              </p>
            </div>
          )
        )}
      </div>

      <div
        className="
          mt-6
          rounded-xl
          border
          border-emerald-400/20
          bg-emerald-400/5
          p-4
          text-sm
          text-emerald-300
        "
      >
        ✓ Your O*NET profile is
        complete and will be used
        to personalize your Deep
        Audit career pathways.
      </div>
    </section>
  );
}